/*
 * Buying Journey orchestrator
 *
 * Owns progress, state and step routing for the guided buying flow.
 * Deliberately namespaced under window.CatnetsJourney and free of globals,
 * because the older selection-* engine puts everything on window and the two
 * can end up on the same page.
 *
 * Step implementations stay decoupled. They listen for journey events and
 * report back with journey.complete(id, data) / journey.clear(id). They never
 * touch internal state directly.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'catnets:buying-journey';
  var STATE_VERSION = 1;

  // A journey older than this is treated as abandoned and started fresh.
  var MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30;

  var EVENTS = {
    ready: 'journey:ready',
    change: 'journey:change',
    enter: 'journey:step:enter',
    leave: 'journey:step:leave',
    complete: 'journey:step:complete',
    skip: 'journey:step:skip',
    reset: 'journey:reset'
  };

  function now() {
    return Date.now();
  }

  function readStore() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      var parsed = JSON.parse(raw);
      if (!parsed || parsed.v !== STATE_VERSION) return null;
      if (!parsed.updatedAt || now() - parsed.updatedAt > MAX_AGE_MS) return null;

      return parsed;
    } catch (err) {
      // Private mode, quota, or corrupt payload. Start clean rather than throw.
      return null;
    }
  }

  function writeStore(state) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (err) {
      /* Non-fatal. The journey still works, it just will not survive a reload. */
    }
  }

  function clearStore() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      /* Non-fatal. */
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {Array<{id:string,label:string,skippable:boolean,skipPrompt:string}>} steps
   */
  function Journey(root, steps) {
    this.root = root;
    this.steps = steps;
    this.stepIndex = {};

    for (var i = 0; i < steps.length; i++) {
      this.stepIndex[steps[i].id] = i;
    }

    this.state = this.load();
    this.panes = {};
    this.bindPanes();
  }

  Journey.prototype.load = function () {
    var stored = readStore();
    var state = {
      v: STATE_VERSION,
      startedAt: now(),
      updatedAt: now(),
      current: this.steps.length ? this.steps[0].id : null,
      exitPromptsSeen: [],
      steps: {}
    };

    for (var i = 0; i < this.steps.length; i++) {
      state.steps[this.steps[i].id] = { complete: false, skipped: false, data: null };
    }

    if (!stored) return state;

    // Merge stored values in by id so adding or reordering steps in the theme
    // editor never strands a customer on a step that no longer exists.
    state.startedAt = stored.startedAt || state.startedAt;
    state.exitPromptsSeen = stored.exitPromptsSeen || [];

    for (var id in state.steps) {
      if (stored.steps && stored.steps[id]) {
        state.steps[id] = {
          complete: !!stored.steps[id].complete,
          skipped: !!stored.steps[id].skipped,
          data: typeof stored.steps[id].data === 'undefined' ? null : stored.steps[id].data
        };
      }
    }

    if (stored.current && this.stepIndex.hasOwnProperty(stored.current)) {
      state.current = stored.current;
    }

    // Never restore someone past a step they have not settled.
    var firstOpen = this.firstUnsettled(state);
    if (firstOpen !== null && this.stepIndex[firstOpen] < this.stepIndex[state.current]) {
      state.current = firstOpen;
    }

    return state;
  };

  Journey.prototype.save = function () {
    this.state.updatedAt = now();
    writeStore(this.state);
  };

  /** A step is settled once it is complete or deliberately skipped. */
  Journey.prototype.isSettled = function (id, state) {
    var s = (state || this.state).steps[id];
    return !!s && (s.complete || s.skipped);
  };

  Journey.prototype.firstUnsettled = function (state) {
    for (var i = 0; i < this.steps.length; i++) {
      if (!this.isSettled(this.steps[i].id, state)) return this.steps[i].id;
    }
    return null;
  };

  /**
   * Forward movement is gated. A customer may revisit anything they have
   * already settled, but may not jump ahead of the first open step.
   */
  /**
   * Nothing past the first unsettled step is reachable, even if it was
   * completed earlier. When a measurement change invalidates the netting,
   * everything behind that gap locks until it is re-done; previously a
   * customer could jump over the hole to a later ticked step and strand
   * themselves on a Continue button that had nowhere to go.
   */
  Journey.prototype.canEnter = function (id) {
    if (!this.stepIndex.hasOwnProperty(id)) return false;

    var firstOpen = this.firstUnsettled();
    return firstOpen === null || this.stepIndex[id] <= this.stepIndex[firstOpen];
  };

  Journey.prototype.goTo = function (id) {
    if (!this.canEnter(id) || id === this.state.current) return false;

    var from = this.state.current;
    this.emit(EVENTS.leave, { step: from });

    this.state.current = id;
    this.save();

    this.render();
    this.emit(EVENTS.enter, { step: id, from: from });
    this.emit(EVENTS.change, { step: id });

    // Every step change starts at the top of the page. Without this, Continue
    // leaves the customer wherever the previous step's scroll position was,
    // often past the new step's content entirely.
    this.scrollToTop();

    return true;
  };

  Journey.prototype.scrollToTop = function () {
    try {
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, left: 0, behavior: reduced ? 'auto' : 'smooth' });
    } catch (err) {
      try {
        window.scrollTo(0, 0);
      } catch (e) {
        /* jsdom and very old browsers: scrolling is cosmetic, never fatal. */
      }
    }
  };

  Journey.prototype.next = function () {
    var i = this.stepIndex[this.state.current];
    if (i < this.steps.length - 1) return this.goTo(this.steps[i + 1].id);
    return false;
  };

  Journey.prototype.back = function () {
    var i = this.stepIndex[this.state.current];
    if (i > 0) return this.goTo(this.steps[i - 1].id);
    return false;
  };

  /** Called by a step implementation once the customer has made valid choices. */
  Journey.prototype.complete = function (id, data) {
    if (!this.stepIndex.hasOwnProperty(id)) return;

    this.state.steps[id] = { complete: true, skipped: false, data: data || null };
    this.save();

    this.render();
    this.emit(EVENTS.complete, { step: id, data: data || null });
  };

  /**
   * Called when a step's selections become invalid, usually because an earlier
   * answer changed underneath it. Unlike the old engine this only clears the
   * steps that actually declared a dependency, rather than everything after.
   */
  Journey.prototype.clear = function (id) {
    if (!this.stepIndex.hasOwnProperty(id)) return;

    this.state.steps[id] = { complete: false, skipped: false, data: null };
    this.save();
    this.render();
  };

  Journey.prototype.step = function (id) {
    return this.steps[this.stepIndex[id]] || null;
  };

  Journey.prototype.data = function (id) {
    var s = this.state.steps[id];
    return s ? s.data : null;
  };

  /**
   * Skip a skippable step. The first attempt on any given step raises the exit
   * upsell instead of skipping. A second attempt goes through. Kurt's brief:
   * one prompt only, so we remember which steps have already asked.
   */
  Journey.prototype.skip = function (id, opts) {
    var config = this.step(id);
    if (!config || !config.skippable) return false;

    var force = opts && opts.force;
    var alreadyAsked = this.state.exitPromptsSeen.indexOf(id) !== -1;

    if (!force && !alreadyAsked) {
      this.state.exitPromptsSeen.push(id);
      this.save();
      this.emit(EVENTS.skip, { step: id, prompted: true, config: config });
      return false;
    }

    this.state.steps[id] = { complete: false, skipped: true, data: null };
    this.save();

    this.emit(EVENTS.skip, { step: id, prompted: false, config: config });
    this.next();
    return true;
  };

  Journey.prototype.reset = function () {
    clearStore();
    this.state = this.load();
    this.save();
    this.render();
    this.emit(EVENTS.reset, {});
  };

  Journey.prototype.emit = function (name, detail) {
    detail = detail || {};
    detail.journey = this;
    this.root.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  };

  Journey.prototype.bindPanes = function () {
    var panes = this.root.querySelectorAll('[data-journey-pane]');
    for (var i = 0; i < panes.length; i++) {
      this.panes[panes[i].getAttribute('data-journey-pane')] = panes[i];
    }
  };

  Journey.prototype.render = function () {
    var current = this.state.current;

    for (var id in this.panes) {
      var isCurrent = id === current;
      this.panes[id].hidden = !isCurrent;
      this.panes[id].setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
    }

    this.renderProgress();
    this.renderNav();
  };

  Journey.prototype.renderProgress = function () {
    var nodes = this.root.querySelectorAll('[data-journey-progress-step]');
    var currentIdx = this.stepIndex[this.state.current];

    // Counted from state, not from the rendered nodes, so the bar stays honest
    // if the progress markers are hidden or a step has no marker.
    var settled = 0;
    for (var t = 0; t < this.steps.length; t++) {
      if (this.isSettled(this.steps[t].id)) settled++;
    }

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var id = node.getAttribute('data-journey-progress-step');
      var s = this.state.steps[id];
      var status;

      if (!s) continue;

      if (s.complete) {
        status = 'complete';
      } else if (s.skipped) {
        status = 'skipped';
      } else if (id === this.state.current) {
        status = 'current';
      } else if (this.stepIndex[id] < currentIdx) {
        status = 'incomplete';
      } else {
        status = 'locked';
      }

      node.setAttribute('data-status', status);
      node.setAttribute('aria-current', id === this.state.current ? 'step' : 'false');

      var reachable = this.canEnter(id);
      node.disabled = !reachable;
      node.setAttribute('aria-disabled', reachable ? 'false' : 'true');
    }

    var pct = this.steps.length ? Math.round((settled / this.steps.length) * 100) : 0;
    var bars = this.root.querySelectorAll('[data-journey-progress-bar]');
    for (var b = 0; b < bars.length; b++) {
      bars[b].style.setProperty('--journey-progress', pct + '%');
      bars[b].setAttribute('aria-valuenow', String(pct));
    }
  };

  Journey.prototype.renderNav = function () {
    var idx = this.stepIndex[this.state.current];
    var config = this.step(this.state.current);
    var settled = this.isSettled(this.state.current);
    var skippable = !!(config && config.skippable);

    var backs = this.root.querySelectorAll('[data-journey-back]');
    for (var i = 0; i < backs.length; i++) {
      backs[i].hidden = idx === 0;
    }

    // An optional step can always be moved past. When nothing has been added,
    // Continue itself acts as skip and routes through the exit upsell, so the
    // customer is never stuck behind a greyed-out button on a step they were
    // told they could skip.
    var nexts = this.root.querySelectorAll('[data-journey-next]');
    for (var n = 0; n < nexts.length; n++) {
      nexts[n].disabled = !(settled || skippable);
      nexts[n].hidden = idx === this.steps.length - 1;
    }

    // The explicit skip link is only useful while the step is skippable and
    // empty. Once something is added, Continue is the clear action.
    var skips = this.root.querySelectorAll('[data-journey-skip]');
    for (var s = 0; s < skips.length; s++) {
      skips[s].hidden = !(skippable && !settled);
    }
  };

  Journey.prototype.listen = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var el = event.target.closest(
        '[data-journey-next],[data-journey-back],[data-journey-skip],[data-journey-reset],[data-journey-progress-step],[data-journey-goto]'
      );
      if (!el || el.disabled) return;

      if (el.hasAttribute('data-journey-reset')) {
        event.preventDefault();

        var message = el.getAttribute('data-journey-reset') ||
          'Start again from scratch? This clears your measurements and choices.';

        if (window.confirm(message)) {
          self.reset();

          // A full reload gives every step a pristine UI (inputs, selections)
          // rather than trusting each one to hear the reset event.
          try {
            window.location.reload();
          } catch (err) {
            /* jsdom cannot navigate; state is already reset above. */
          }
        }
        return;
      }

      if (el.hasAttribute('data-journey-next')) {
        event.preventDefault();

        // On a skippable step with nothing added, Continue is a skip, so it
        // raises the exit upsell rather than silently moving on.
        var current = self.state.current;
        var config = self.step(current);
        if (config && config.skippable && !self.isSettled(current)) {
          self.skip(current);
        } else {
          self.next();
        }
      } else if (el.hasAttribute('data-journey-back')) {
        event.preventDefault();
        self.back();
      } else if (el.hasAttribute('data-journey-skip')) {
        event.preventDefault();
        self.skip(self.state.current, { force: el.hasAttribute('data-journey-skip-force') });
      } else if (el.hasAttribute('data-journey-progress-step')) {
        event.preventDefault();
        self.goTo(el.getAttribute('data-journey-progress-step'));
      } else if (el.hasAttribute('data-journey-goto')) {
        event.preventDefault();
        self.goTo(el.getAttribute('data-journey-goto'));
      }
    });
  };

  /**
   * Exit upsell.
   *
   * Journey.skip() refuses the first skip attempt on a step and emits
   * skip with prompted:true. This controller turns that into the modal, and
   * a confirmed skip re-calls skip() with force. One prompt per step, which is
   * the brief: ask once, then respect the answer.
   */
  function ExitPrompt(root, journey) {
    this.root = root;
    this.journey = journey;
    this.el = root.querySelector('[data-journey-modal]');
    this.lastFocus = null;

    if (!this.el) return;

    this.bind();
  }

  ExitPrompt.prototype.bind = function () {
    var self = this;

    this.root.addEventListener(EVENTS.skip, function (event) {
      if (!event.detail.prompted) return;
      self.open(event.detail.step);
    });

    this.el.addEventListener('click', function (event) {
      // Clicking the backdrop is a dismissal, same as choosing to stay.
      if (event.target === self.el || event.target.closest('[data-journey-modal-dismiss]')) {
        event.preventDefault();
        self.close();
        return;
      }

      if (event.target.closest('[data-journey-modal-confirm]')) {
        event.preventDefault();
        var step = self.el.getAttribute('data-journey-modal-step');
        self.close();
        if (step) self.journey.skip(step, { force: true });
      }
    });

    document.addEventListener('keydown', function (event) {
      if (!self.isOpen()) return;

      if (event.key === 'Escape') {
        self.close();
      } else if (event.key === 'Tab') {
        self.trap(event);
      }
    });
  };

  ExitPrompt.prototype.isOpen = function () {
    return this.el && this.el.classList.contains('is-open');
  };

  ExitPrompt.prototype.focusable = function () {
    return this.el.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])');
  };

  ExitPrompt.prototype.trap = function (event) {
    var items = this.focusable();
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  ExitPrompt.prototype.open = function (stepId) {
    if (!this.el) return;

    // Each step carries its own copy. Show only the panel for this step.
    var panels = this.el.querySelectorAll('[data-journey-modal-panel]');
    var matched = false;

    for (var i = 0; i < panels.length; i++) {
      var isMatch = panels[i].getAttribute('data-journey-modal-panel') === stepId;
      panels[i].hidden = !isMatch;
      if (isMatch) matched = true;
    }

    // No copy authored for this step, so there is nothing to say. Skip straight
    // through rather than showing an empty box.
    if (!matched) {
      this.journey.skip(stepId, { force: true });
      return;
    }

    this.lastFocus = document.activeElement;
    this.el.setAttribute('data-journey-modal-step', stepId);
    this.el.classList.add('is-open');
    this.el.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';

    var items = this.focusable();
    if (items.length) items[0].focus();
  };

  ExitPrompt.prototype.close = function () {
    if (!this.el) return;

    this.el.classList.remove('is-open');
    this.el.setAttribute('aria-hidden', 'true');
    this.el.removeAttribute('data-journey-modal-step');
    document.documentElement.style.overflow = '';

    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    this.lastFocus = null;
  };

  /**
   * Steps are authored as their own sections so each can carry its own schema
   * and blocks. Shopify renders them as siblings of the journey, so we adopt
   * them into the matching pane slot here.
   *
   * Re-run on shopify:section:load, because the theme editor re-renders a
   * section back in its original position when a merchant edits it.
   */
  function adoptSteps(root) {
    var sources = document.querySelectorAll('[data-journey-step-source]');

    for (var i = 0; i < sources.length; i++) {
      var source = sources[i];
      var id = source.getAttribute('data-journey-step-source');
      var slot = root.querySelector('[data-journey-slot="' + id + '"]');

      if (!slot || slot.contains(source)) continue;

      var placeholder = slot.querySelector('.journey__placeholder');
      if (placeholder) placeholder.remove();

      slot.appendChild(source);
    }
  }

  function boot() {
    var root = document.querySelector('[data-journey-root]');
    if (!root) return;

    adoptSteps(root);

    document.addEventListener('shopify:section:load', function () {
      adoptSteps(root);
      if (window.CatnetsJourney) window.CatnetsJourney.render();
    });

    var steps = [];
    var nodes = root.querySelectorAll('[data-journey-pane]');

    for (var i = 0; i < nodes.length; i++) {
      steps.push({
        id: nodes[i].getAttribute('data-journey-pane'),
        label: nodes[i].getAttribute('data-journey-label') || '',
        skippable: nodes[i].hasAttribute('data-journey-skippable'),
        skipPrompt: nodes[i].getAttribute('data-journey-skip-prompt') || ''
      });
    }

    if (!steps.length) return;

    var journey = new Journey(root, steps);
    journey.listen();

    // Bound before the first render so a restored journey that lands on a
    // skippable step still has its prompt wired.
    new ExitPrompt(root, journey);

    journey.render();

    window.CatnetsJourney = journey;
    journey.emit(EVENTS.ready, {});
  }

  window.CatnetsJourneyEvents = EVENTS;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
